import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { BRAND } from "../lib/brand";
import { authApi, login, fetchMe, changePassword, getUser, getToken, clearSession, formatApiErrorDetail } from "../lib/auth";
import {
  Lock, Plus, Pencil, Trash2, Eye, EyeOff, LogOut, Package, Users, KeyRound, ShieldCheck,
} from "lucide-react";

const emptyProduct = { name: "", name_id: "", wholesale: "", retail: "", margin: "", img: "", tag: "tag1", order: 100, active: true };

export default function Admin() {
  const [user, setUser] = useState(getUser());
  const [authed, setAuthed] = useState(!!getToken());
  const [tab, setTab] = useState("products");

  useEffect(() => {
    if (!getToken()) return;
    fetchMe()
      .then((u) => { setUser(u); setAuthed(true); })
      .catch(() => { clearSession(); setAuthed(false); });
  }, []);

  if (!authed) return <Login onSuccess={(u) => { setUser(u); setAuthed(true); }} />;

  const isSuper = user?.role === "super-admin";

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="bg-white border-b border-soft sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={BRAND.logoIcon} alt="AsiaCommerce" className="h-8 w-8 object-contain" />
            <div>
              <div className="font-display font-bold text-sm text-ink">Admin Panel</div>
              <div className="text-[10px] text-ink-muted">{user?.email} · <span className="text-brand-red font-semibold">{user?.role}</span></div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="text-sm text-ink-muted hover:text-ink px-3 py-2">View site</Link>
            <Button variant="outline" onClick={() => { clearSession(); setAuthed(false); }} className="rounded-full h-10 border-soft text-sm" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border border-soft rounded-full p-1 mb-6">
            <TabsTrigger value="products" data-testid="tab-products" className="rounded-full data-[state=active]:bg-brand-red data-[state=active]:text-white">
              <Package className="w-4 h-4 mr-1.5" /> Products
            </TabsTrigger>
            {isSuper && (
              <TabsTrigger value="users" data-testid="tab-users" className="rounded-full data-[state=active]:bg-brand-red data-[state=active]:text-white">
                <Users className="w-4 h-4 mr-1.5" /> Admins
              </TabsTrigger>
            )}
            <TabsTrigger value="account" data-testid="tab-account" className="rounded-full data-[state=active]:bg-brand-red data-[state=active]:text-white">
              <KeyRound className="w-4 h-4 mr-1.5" /> Account
            </TabsTrigger>
          </TabsList>
          <TabsContent value="products"><ProductsPanel /></TabsContent>
          {isSuper && <TabsContent value="users"><UsersPanel currentUserId={user.id} /></TabsContent>}
          <TabsContent value="account"><AccountPanel user={user} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ===================== Login =====================
function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, pwd);
      toast.success(`Selamat datang, ${u.name}`);
      onSuccess(u);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Login gagal");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-5" data-testid="admin-login">
      <div className="max-w-md w-full bg-white rounded-3xl border border-soft p-8 soft-shadow">
        <div className="flex items-center gap-2 mb-6">
          <img src={BRAND.logoIcon} alt="AsiaCommerce" className="h-10 w-10 object-contain" />
          <div>
            <div className="font-display font-extrabold text-lg text-ink">Admin Panel</div>
            <div className="text-xs text-ink-muted">AsiaCommerce Hub</div>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-sm font-semibold text-ink">Email</Label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className="h-12 rounded-xl border-soft mt-1.5" placeholder="email@asiacommerce.net" />
          </div>
          <div>
            <Label className="text-sm font-semibold text-ink">Password</Label>
            <div className="relative mt-1.5">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-muted" />
              <Input type={showPwd ? "text" : "password"} required value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="login-password" className="h-12 rounded-xl border-soft pl-10 pr-10" />
              <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink" tabIndex={-1}>
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full h-12 rounded-full bg-brand-red hover:bg-[#A60D25] text-white font-semibold" data-testid="login-submit-btn">
            {loading ? "Memverifikasi…" : "Login"}
          </Button>
          <div className="flex items-center justify-between text-xs">
            <Link to="/admin/forgot-password" className="text-ink-muted hover:text-brand-red">Lupa password?</Link>
            <Link to="/" className="text-ink-muted hover:text-brand-red">← Beranda</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

// ===================== Products Panel =====================
function ProductsPanel() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const { data } = await authApi.get("/admin/products");
      setProducts(data);
    } catch { toast.error("Gagal memuat produk"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchProducts(); }, []);

  const openCreate = () => { setEditing(null); setForm({ ...emptyProduct, order: products.length + 1 }); setDialogOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ name: p.name, name_id: p.name_id, wholesale: p.wholesale, retail: p.retail, margin: p.margin, img: p.img, tag: p.tag, order: p.order, active: p.active }); setDialogOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await authApi.patch(`/admin/products/${editing.id}`, form);
      else await authApi.post(`/admin/products`, form);
      toast.success(editing ? "Produk diperbarui" : "Produk dibuat");
      setDialogOpen(false);
      await fetchProducts();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const doDelete = async () => {
    try {
      await authApi.delete(`/admin/products/${confirmDelete.id}`);
      toast.success("Produk dihapus");
      setConfirmDelete(null);
      await fetchProducts();
    } catch { toast.error("Gagal menghapus"); }
  };

  return (
    <div data-testid="products-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-extrabold text-2xl text-ink flex items-center gap-2"><Package className="w-6 h-6" /> Products</h1>
          <p className="text-sm text-ink-muted mt-1">{products.length} product{products.length !== 1 ? "s" : ""} total</p>
        </div>
        <Button onClick={openCreate} className="rounded-full bg-brand-red hover:bg-[#A60D25] text-white font-semibold h-11 px-5" data-testid="admin-create-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Product
        </Button>
      </div>

      {loading && products.length === 0 ? (
        <div className="text-center py-20 text-ink-muted">Loading…</div>
      ) : (
        <div className="bg-white rounded-2xl border border-soft overflow-hidden" data-testid="admin-product-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-cream">
                <tr className="text-left text-[11px] uppercase tracking-wider text-ink-muted">
                  <th className="px-4 py-3 font-bold">#</th>
                  <th className="px-4 py-3 font-bold">Image</th>
                  <th className="px-4 py-3 font-bold">Name (EN / ID)</th>
                  <th className="px-4 py-3 font-bold">Wholesale</th>
                  <th className="px-4 py-3 font-bold">Retail</th>
                  <th className="px-4 py-3 font-bold">Margin</th>
                  <th className="px-4 py-3 font-bold">Tag</th>
                  <th className="px-4 py-3 font-bold">Status</th>
                  <th className="px-4 py-3 font-bold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-soft">
                {products.map((p) => (
                  <tr key={p.id} data-testid={`admin-row-${p.id}`} className="hover:bg-cream/60">
                    <td className="px-4 py-3 font-semibold">{p.order}</td>
                    <td className="px-4 py-3"><img src={p.img} alt="" className="w-12 h-12 rounded-lg object-cover border border-soft" /></td>
                    <td className="px-4 py-3"><div className="font-semibold">{p.name}</div><div className="text-xs text-ink-muted">{p.name_id}</div></td>
                    <td className="px-4 py-3">{p.wholesale}</td>
                    <td className="px-4 py-3">{p.retail}</td>
                    <td className="px-4 py-3"><span className="px-2 py-1 rounded-full bg-brand-red text-white text-xs font-bold">{p.margin}</span></td>
                    <td className="px-4 py-3"><span className="px-2 py-1 rounded-full bg-accent-subtle text-brand-red text-xs font-semibold uppercase">{p.tag}</span></td>
                    <td className="px-4 py-3">{p.active ? <span className="px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold">Active</span> : <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold">Hidden</span>}</td>
                    <td className="px-4 py-3 text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`admin-edit-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(p)} data-testid={`admin-delete-${p.id}`} className="text-brand-red hover:text-brand-red hover:bg-accent-subtle"><Trash2 className="w-4 h-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Product" : "Create New Product"}</DialogTitle>
            <DialogDescription>Changes apply instantly to landing page + catalog.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Name (EN) *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Name (ID) *</Label><Input required value={form.name_id} onChange={(e) => setForm({ ...form, name_id: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div><Label>Wholesale *</Label><Input required value={form.wholesale} onChange={(e) => setForm({ ...form, wholesale: e.target.value })} placeholder="Rp 45.000" className="mt-1.5" /></div>
              <div><Label>Retail *</Label><Input required value={form.retail} onChange={(e) => setForm({ ...form, retail: e.target.value })} placeholder="Rp 149.000" className="mt-1.5" /></div>
              <div><Label>Margin *</Label><Input required value={form.margin} onChange={(e) => setForm({ ...form, margin: e.target.value })} placeholder="+230%" className="mt-1.5" /></div>
            </div>
            <div>
              <Label>Image URL *</Label>
              <Textarea required value={form.img} onChange={(e) => setForm({ ...form, img: e.target.value })} className="mt-1.5 min-h-[68px]" />
              {form.img && <img src={form.img} alt="preview" className="mt-2 w-28 h-28 rounded-lg object-cover border border-soft" />}
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label>Tag</Label>
                <Select value={form.tag} onValueChange={(v) => setForm({ ...form, tag: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tag1">Hot Pick</SelectItem>
                    <SelectItem value="tag2">New Arrival</SelectItem>
                    <SelectItem value="tag3">Bestseller</SelectItem>
                    <SelectItem value="tag4">Limited</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Order</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className="mt-1.5" /></div>
              <div>
                <Label>Status</Label>
                <Select value={form.active ? "1" : "0"} onValueChange={(v) => setForm({ ...form, active: v === "1" })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="1">Active</SelectItem><SelectItem value="0">Hidden</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-brand-red hover:bg-[#A60D25] text-white">{editing ? "Update" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus produk?</AlertDialogTitle>
            <AlertDialogDescription>"{confirmDelete?.name_id || confirmDelete?.name}" akan dihapus permanen.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-brand-red hover:bg-[#A60D25]">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ===================== Users Panel =====================
function UsersPanel({ currentUserId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "admin" });
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchUsers = async () => {
    setLoading(true);
    try { setUsers((await authApi.get("/admin/users")).data); } catch { toast.error("Gagal memuat admins"); } finally { setLoading(false); }
  };
  useEffect(() => { fetchUsers(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await authApi.post("/admin/users", form);
      // Show temp password so super-admin can share it manually if email delivery fails
      toast.success(
        `Admin diundang. Email + password sementara dikirim ke ${form.email}.\n\nPassword sementara: ${data.temp_password}`,
        { duration: 20000 }
      );
      setDialogOpen(false);
      setForm({ email: "", name: "", role: "admin" });
      await fetchUsers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const remove = async () => {
    try {
      await authApi.delete(`/admin/users/${confirmDelete.id}`);
      toast.success("Admin dihapus");
      setConfirmDelete(null);
      await fetchUsers();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  return (
    <div data-testid="users-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-extrabold text-2xl text-ink flex items-center gap-2"><Users className="w-6 h-6" /> Admin Users</h1>
          <p className="text-sm text-ink-muted mt-1">{users.length} admin{users.length !== 1 ? "s" : ""}</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="rounded-full bg-brand-red hover:bg-[#A60D25] text-white font-semibold h-11 px-5" data-testid="admin-invite-btn">
          <Plus className="w-4 h-4 mr-2" /> Invite Admin
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-soft overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-users-table">
          <thead className="bg-cream">
            <tr className="text-left text-[11px] uppercase tracking-wider text-ink-muted">
              <th className="px-4 py-3 font-bold">Email</th>
              <th className="px-4 py-3 font-bold">Name</th>
              <th className="px-4 py-3 font-bold">Role</th>
              <th className="px-4 py-3 font-bold">Created</th>
              <th className="px-4 py-3 font-bold text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-soft">
            {users.map((u) => (
              <tr key={u.id} data-testid={`admin-user-row-${u.id}`}>
                <td className="px-4 py-3 font-semibold">{u.email} {u.id === currentUserId && <span className="ml-1 text-xs text-brand-orange">(you)</span>}</td>
                <td className="px-4 py-3">{u.name}</td>
                <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-bold ${u.role === "super-admin" ? "bg-brand-red text-white" : "bg-accent-subtle text-brand-red"}`}>{u.role}</span></td>
                <td className="px-4 py-3 text-ink-muted text-xs">{new Date(u.created_at).toLocaleDateString("id-ID")}</td>
                <td className="px-4 py-3 text-right">
                  {u.id !== currentUserId && (
                    <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(u)} data-testid={`admin-user-delete-${u.id}`} className="text-brand-red hover:text-brand-red hover:bg-accent-subtle"><Trash2 className="w-4 h-4" /></Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && <div className="text-center py-10 text-ink-muted">Loading…</div>}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Undang Admin Baru</DialogTitle>
            <DialogDescription>Password sementara akan dikirim via email. Admin baru harus ganti password setelah login pertama.</DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div><Label>Email *</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="invite-email" /></div>
            <div><Label>Nama *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="invite-name" /></div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1.5" data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="admin">Admin</SelectItem><SelectItem value="super-admin">Super Admin</SelectItem></SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-brand-red hover:bg-[#A60D25] text-white" data-testid="invite-submit-btn">Kirim Undangan</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus admin?</AlertDialogTitle>
            <AlertDialogDescription>{confirmDelete?.email} tidak akan bisa login lagi.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-brand-red hover:bg-[#A60D25]">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ===================== Account Panel =====================
function AccountPanel({ user }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("Password minimal 8 karakter");
    if (next !== confirm) return toast.error("Password baru dan konfirmasi tidak sama");
    setLoading(true);
    try {
      await changePassword(current, next);
      toast.success("Password berhasil diubah");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="account-panel" className="max-w-2xl">
      <h1 className="font-display font-extrabold text-2xl text-ink flex items-center gap-2 mb-2">
        <ShieldCheck className="w-6 h-6" /> Akun Saya
      </h1>
      <p className="text-sm text-ink-muted mb-6">Kelola password Anda. Untuk reset jika lupa, gunakan halaman <Link to="/admin/forgot-password" className="text-brand-red font-semibold">Lupa Password</Link>.</p>

      <div className="bg-white rounded-2xl border border-soft p-6 mb-6">
        <div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-ink-muted">Email</span><span className="font-semibold">{user?.email}</span></div>
          <div className="flex justify-between"><span className="text-ink-muted">Nama</span><span className="font-semibold">{user?.name}</span></div>
          <div className="flex justify-between"><span className="text-ink-muted">Role</span><span className="font-semibold text-brand-red">{user?.role}</span></div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-soft p-6">
        <h2 className="font-display font-bold text-lg text-ink mb-1">Ganti Password</h2>
        <p className="text-xs text-ink-muted mb-5">Password baru minimal 8 karakter.</p>
        <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
          <div><Label>Password lama *</Label><Input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} className="mt-1.5 h-11" data-testid="cp-current" /></div>
          <div><Label>Password baru *</Label><Input type="password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} className="mt-1.5 h-11" data-testid="cp-next" /></div>
          <div><Label>Konfirmasi password baru *</Label><Input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 h-11" data-testid="cp-confirm" /></div>
          <Button type="submit" disabled={loading} className="rounded-full bg-brand-red hover:bg-[#A60D25] text-white font-semibold h-11 px-6" data-testid="cp-submit-btn">
            {loading ? "Menyimpan…" : "Update Password"}
          </Button>
        </form>
      </div>
    </div>
  );
}
